import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { V2AuthProvider, useV2Auth } from '@/context/V2AuthContext';
import { useTranslation } from 'react-i18next';
import V2Header from '@/components/V2Header';
import V2Footer from '@/components/V2Footer';
import LandingV2 from '@/pages/LandingV2';
import AuthV2 from '@/pages/AuthV2';
import AuthCallbackV2 from '@/pages/AuthCallbackV2';
import GiveV2 from '@/pages/GiveV2';
import GiveJourneyV2 from '@/pages/GiveJourneyV2';
import InboxV2 from '@/pages/InboxV2';
import FuerDichV2 from '@/pages/FuerDichV2';
import IchV2 from '@/pages/IchV2';
import BewahrtV2 from '@/pages/BewahrtV2';
import MomentsListV2 from '@/pages/MomentsListV2';
import MomentCreateV2 from '@/pages/MomentCreateV2';
import MomentManageV2 from '@/pages/MomentManageV2';
import MomentInviteV2 from '@/pages/MomentInviteV2';
import MomentExperienceV2 from '@/pages/MomentExperienceV2';
import FeedV2 from '@/pages/FeedV2';
import ProfileV2 from '@/pages/ProfileV2';
import ImpressumV2 from '@/pages/ImpressumV2';
import DatenschutzV2 from '@/pages/DatenschutzV2';
import BetaGuideV2 from '@/pages/BetaGuideV2';
import UeberUnsV2 from '@/pages/UeberUnsV2';
import AdminV2 from '@/pages/AdminV2';
import SharedCarmaV2 from '@/pages/SharedCarmaV2';
import GratitudeV2 from '@/pages/GratitudeV2';
import PublicProfileV2 from '@/pages/PublicProfileV2';
import CarmaChainV2 from '@/pages/CarmaChainV2';
import TagebuchV2 from '@/pages/TagebuchV2';
import EntdeckenV2 from '@/pages/EntdeckenV2';
import MaskPreviewV2 from '@/pages/MaskPreviewV2';
import FeedbackButton from '@/components/FeedbackButton';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';

function RequireAuth({ children }) {
  const { user, initialLoading } = useV2Auth();
  const { t } = useTranslation();
  const location = useLocation();
  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
        <div className="text-sm text-[var(--muted)]">{t('common.loading')}</div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to={`/auth?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }
  return children;
}

function HandleRoute() {
  const { handle } = useParams();
  if (handle?.startsWith('@') && handle.length > 1) {
    return <PublicProfileV2 username={handle.slice(1)} />;
  }
  return <Navigate to="/" replace />;
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();
  // Detect OAuth callback: session_id in URL fragment — handle BEFORE normal routing
  if (location.hash?.includes('session_id=')) {
    return <AuthCallbackV2 />;
  }
  return (
    <Routes>
      {/* Admin route - outside normal layout */}
      <Route path="/admin" element={<AdminV2 />} />
      
      {/* All other routes with normal layout */}
      <Route path="/*" element={
        <>
          <V2Header />
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<LandingV2 />} />
              <Route path="/auth" element={<AuthV2 />} />
              <Route path="/give" element={<GiveJourneyV2 />} />
              <Route
                path="/give-classic"
                element={(
                  <RequireAuth>
                    <GiveV2 />
                  </RequireAuth>
                )}
              />
              <Route
                path="/inbox"
                element={(
                  <RequireAuth>
                    <FuerDichV2 />
                  </RequireAuth>
                )}
              />
              <Route
                path="/inbox-classic"
                element={(
                  <RequireAuth>
                    <InboxV2 />
                  </RequireAuth>
                )}
              />
              <Route
                path="/feed"
                element={(
                  <RequireAuth>
                    <FeedV2 />
                  </RequireAuth>
                )}
              />
              <Route
                path="/dankbar"
                element={(
                  <RequireAuth>
                    <GratitudeV2 />
                  </RequireAuth>
                )}
              />
              <Route
                path="/mask-preview"
                element={(
                  <RequireAuth>
                    <MaskPreviewV2 />
                  </RequireAuth>
                )}
              />
              <Route
                path="/entdecken"
                element={(
                  <RequireAuth>
                    <EntdeckenV2 />
                  </RequireAuth>
                )}
              />
              <Route
                path="/tagebuch"
                element={(
                  <RequireAuth>
                    <TagebuchV2 />
                  </RequireAuth>
                )}
              />
              <Route
                path="/profile"
                element={(
                  <RequireAuth>
                    <IchV2 />
                  </RequireAuth>
                )}
              />
              <Route
                path="/profile-classic"
                element={(
                  <RequireAuth>
                    <ProfileV2 />
                  </RequireAuth>
                )}
              />
              <Route
                path="/bewahrt"
                element={(
                  <RequireAuth>
                    <BewahrtV2 />
                  </RequireAuth>
                )}
              />
              <Route path="/momente" element={<RequireAuth><MomentsListV2 /></RequireAuth>} />
              <Route path="/momente/neu" element={<RequireAuth><MomentCreateV2 /></RequireAuth>} />
              <Route path="/momente/:id" element={<RequireAuth><MomentManageV2 /></RequireAuth>} />
              <Route path="/moment/einladung/:token" element={<MomentInviteV2 />} />
              <Route path="/moment/erleben/:token" element={<MomentExperienceV2 />} />
              <Route
                path="/chain"
                element={(
                  <RequireAuth>
                    <CarmaChainV2 />
                  </RequireAuth>
                )}
              />
              <Route path="/impressum" element={<ImpressumV2 />} />
              <Route path="/datenschutz" element={<DatenschutzV2 />} />
              <Route path="/beta-guide" element={<BetaGuideV2 />} />
              <Route path="/ueber-uns" element={<UeberUnsV2 />} />
              <Route path="/carma/shared/:token" element={<SharedCarmaV2 />} />
              <Route path="/:handle" element={<HandleRoute />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          <V2Footer />
          <FeedbackButton />
        </>
      } />
    </Routes>
  );
}

export default function AppV2() {
  return (
    <V2AuthProvider>
      <div 
        className="cc-app-bg min-h-screen text-[var(--text)] flex flex-col relative"
        style={{ background: 'var(--bg, #f7f5f1)' }}
      >
        <div className="cc-app-overlay min-h-screen flex flex-col relative">
          <Toaster position="top-right" richColors />
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <PWAInstallPrompt />
        </div>
      </div>
    </V2AuthProvider>
  );
}
